import {
	useBlockProps,
	useInnerBlocksProps,
	store as blockEditorStore,
} from '@wordpress/block-editor';
import { Button } from '@wordpress/components';
import { useState, useEffect, useRef } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';

import { store as blocksStore } from '@wordpress/blocks';
import { __, sprintf } from '@wordpress/i18n';

import Settings from './Settings';
import { TEMPLATE, getAnimationClasses } from '../constants';
import { applyLayoutPreset, REGION_BLOCK } from '../apply-layout-preset';
import { resolvePresetValue } from '../../../utils/helpers';

import '../style.scss';
import '../editor.scss';

/*
 * Mirrors render.php's $css_dim_pattern guard. mediaSize and mediaBorderRadius
 * are free-form strings, and content in the wild carries values like the WP
 * image-size slug "large" — the frontend silently falls back to the default,
 * but an invalid inline `width`/`height` is simply dropped by the browser,
 * which leaves the image at its natural size, overflowing the tab it labels
 * and painting over the rest of the block. Same pattern, same fallback, so the
 * canvas degrades exactly the way the page will.
 */
const CSS_DIM_PATTERN = /^\d+(\.\d+)?(px|rem|em|%)$/;
const cssDimOr = ( value, fallback ) =>
	CSS_DIM_PATTERN.test( String( value || '' ) ) ? value : fallback;

export default function Edit( { attributes, setAttributes, clientId } ) {
	const {
		layoutPreset,
		orientation,
		tabPosition,
		tabAlignment,
		tabWidth,
		activeTabColor,
		activeTabBg,
		activeTabBgGradient,
		hoverTabColor,
		hoverTabBg,
		hoverTabBgGradient,
		inactiveTabBg,
		inactiveTabBgGradient,
		inactiveTabColor,
		tabGap,
		contentPadding,
		dimInactiveTabs,
		activateOn,
	} = attributes;

	const [ activeTab, setActiveTab ] = useState( 0 );

	// Read children's label attributes
	const children = useSelect(
		( select ) => select( blockEditorStore ).getBlocks( clientId ),
		[ clientId ]
	);

	// Auto-switch when a child Tab (or descendant) is selected
	const selectedTabIndex = useSelect(
		( select ) => {
			const { getSelectedBlockClientId, getBlockParents, getBlocks } =
				select( blockEditorStore );
			const selectedId = getSelectedBlockClientId();
			if ( ! selectedId ) {
				return null;
			}

			const innerBlocks = getBlocks( clientId );
			const parents = getBlockParents( selectedId, true );

			for ( let i = 0; i < innerBlocks.length; i++ ) {
				const childId = innerBlocks[ i ].clientId;
				if (
					selectedId === childId ||
					parents.includes( childId )
				) {
					return i;
				}
			}
			return null;
		},
		[ clientId ]
	);

	useEffect( () => {
		if ( selectedTabIndex !== null ) {
			setActiveTab( selectedTabIndex );
		}
	}, [ selectedTabIndex ] );

	const { replaceInnerBlocks } = useDispatch( blockEditorStore );

	/*
	 * Whether the region block type is actually available.
	 *
	 * This gates the sync below, and it is not paranoia — it is the fix for a
	 * real crash. Blocks here are registered by a PHP class per build folder,
	 * not from block.json alone, so a region block with no matching class is
	 * unregistered on both sides. `createBlock` then yields something whose
	 * `area` attribute never matches what the preset asked for, the sync
	 * decides the tab still needs rebuilding, dispatches again, and React tears
	 * the editor down with "Maximum update depth exceeded".
	 *
	 * Bailing out early turns that class of mistake into a layout that quietly
	 * does not apply, which is recoverable, instead of an editor that will not
	 * load.
	 */
	const canUseRegions = useSelect(
		( select ) => !! select( blocksStore ).getBlockType( REGION_BLOCK ),
		[]
	);

	/*
	 * Keep every tab's regions in step with the preset.
	 *
	 * Doing this from one effect rather than from the preset control covers the
	 * case the control cannot see: a tab inserted later arrives with free-form
	 * content and no regions, and would otherwise fall out of the grid. Safe to
	 * re-run because applyLayoutPreset skips tabs that already match — without
	 * that guard this would replace blocks, re-fire on the change, and loop.
	 */
	// Survives renders so a tab is never acted on twice for the same shape.
	const appliedShapes = useRef( new Map() );

	useEffect( () => {
		if ( ! canUseRegions ) {
			return;
		}

		applyLayoutPreset(
			children,
			layoutPreset,
			replaceInnerBlocks,
			appliedShapes.current
		);
	}, [ layoutPreset, children, replaceInnerBlocks, canUseRegions ] );

	// Build CSS vars
	const paddingVars = {};
	if ( contentPadding && typeof contentPadding === 'object' ) {
		if ( contentPadding.top )
			paddingVars[ '--content-padding-top' ] = contentPadding.top;
		if ( contentPadding.right )
			paddingVars[ '--content-padding-right' ] =
				contentPadding.right;
		if ( contentPadding.bottom )
			paddingVars[ '--content-padding-bottom' ] =
				contentPadding.bottom;
		if ( contentPadding.left )
			paddingVars[ '--content-padding-left' ] =
				contentPadding.left;
	}

	const blockProps = useBlockProps( {
		className: [
			'brandy-tabs',
			`tab-orientation-${ orientation }`,
			`tab-position-${ tabPosition }`,
			`tab-align-${ tabAlignment }`,
			`active-tab-${ activeTab }`,
			`layout-${ layoutPreset }`,
			/* Mirrors the root class render.php adds, so the shared style.scss
			   dim rule applies in the canvas without a second implementation. */
			...( dimInactiveTabs ? [ 'dim-inactive-tabs' ] : [] ),
			/* The canvas hides inactive tabs with `display: none` too, so the
			   entrances replay on every switch here just as they do on the
			   frontend — a real preview rather than a second implementation.
			   The panel cross-fade has nothing to act on: the editor renders
			   the tab blocks straight into `__panels` with no
			   `.brandy-tabs__panel` wrapper between them. */
			...getAnimationClasses( attributes ),
		].join( ' ' ),
		style: {
			'--tab-gap': tabGap,
				...( orientation === 'vertical' && tabWidth
					? { '--trigger-width': tabWidth }
					: {} ),
			...( activeTabColor
				? { '--tab-active-color': resolvePresetValue( activeTabColor ) }
				: {} ),
			...( ( activeTabBg || activeTabBgGradient )
				? { '--tab-active-bg': activeTabBgGradient || activeTabBg }
				: {} ),
			...( hoverTabColor
				? { '--tab-hover-color': resolvePresetValue( hoverTabColor ) }
				: {} ),
			...( ( hoverTabBg || hoverTabBgGradient )
				? { '--tab-hover-bg': hoverTabBgGradient || hoverTabBg }
				: {} ),
			...( ( inactiveTabBg || inactiveTabBgGradient )
				? { '--tab-inactive-bg': inactiveTabBgGradient || inactiveTabBg }
				: {} ),
			...( inactiveTabColor
				? { '--tab-inactive-color': resolvePresetValue( inactiveTabColor ) }
				: {} ),
			...paddingVars,
		},
	} );

	const innerBlocksProps = useInnerBlocksProps(
		{ className: 'brandy-tabs__panels' },
		{ template: TEMPLATE }
	);

	// One tab with a link turns the whole strip into links, matching render.php.
	const hasLinks = children.some( ( child ) => !! child.attributes?.url );

	return (
		<>
			<Settings
				attributes={ attributes }
				setAttributes={ setAttributes }
				clientId={ clientId }
			/>
			<div { ...blockProps }>
				{ /*
				 * Mirrors render.php: once any tab carries a link the strip is a
				 * list of links, not a tablist, so the role goes away here too
				 * rather than leaving the canvas claiming semantics the frontend
				 * does not emit.
				 */ }
				<nav
					className="brandy-tabs__nav"
					role={ hasLinks ? undefined : 'tablist' }
				>
					{ children.map( ( child, i ) => {
						const {
							label,
							mediaType,
							iconName,
							iconType,
							mediaUrl,
							mediaAlt,
							mediaSize,
							mediaBorderRadius,
							mediaFit,
							url,
						} = child.attributes;
						const mediaSizeCss = cssDimOr( mediaSize, '24px' );
						const mediaRadiusCss = cssDimOr(
							mediaBorderRadius,
							'0px'
						);
						return (
							<Button
								key={ child.clientId }
								className={ `brandy-tabs__tab${
									i === activeTab ? ' is-active' : ''
								}` }
								/*
								 * The canvas keeps click-to-preview whatever the
								 * frontend trigger is, and never follows the
								 * link — an author selecting a tab to edit it
								 * must not be navigated away from the editor.
								 * Hover activation is previewed by mouseenter
								 * without the delay, since a 300ms wait while
								 * authoring only feels broken.
								 */
								onMouseEnter={
									// render.php forces hover once any tab has a
									// link, since click is spent on navigating;
									// the canvas follows the same rule so the
									// preview matches what will ship.
									activateOn === 'hover' || hasLinks
										? () => setActiveTab( i )
										: undefined
								}
								onClick={ () => setActiveTab( i ) }
								title={
									url
										? sprintf(
												/* translators: %s: destination URL of the tab link. */
												__(
													'Links to %s on the frontend',
													'brandy-blocks'
												),
												url
										  )
										: undefined
								}
							>
								{ mediaType === 'icon' && iconName && (
									<i
										className={ `ph${
											iconType !== 'regular'
												? '-' + iconType
												: ''
										} ph-${ iconName }` }
										style={ {
											fontSize: mediaSizeCss,
											borderRadius: mediaRadiusCss,
										} }
									/>
								) }
								{ mediaType === 'image' && mediaUrl && (
									<img
										src={ mediaUrl }
										alt={ mediaAlt || '' }
										style={ {
											/* Kept in step with the matching
											   branch in render.php, percentage
											   carve-out included: a percentage
											   height has no definite parent
											   height to resolve against, so
											   those keep the squared box and
											   only change how they fit. */
											...( mediaFit !== 'cover' &&
											! mediaSizeCss.endsWith( '%' )
												? {
														height: mediaSizeCss,
														width: 'auto',
														objectFit: 'contain',
												  }
												: {
														width: mediaSizeCss,
														height: mediaSizeCss,
														objectFit:
															mediaFit === 'cover'
																? 'cover'
																: 'contain',
												  } ),
											/* max-width comes from style.scss,
											   which is imported into the canvas
											   too — no need to repeat it here. */
											borderRadius: mediaRadiusCss,
										} }
									/>
								) }
								{ /* Only stand in for a missing label when there
								     is no media actually resolved, so an
								     image-only tab previews exactly as it
								     renders — while a tab mid-authoring (label
								     cleared, image not picked yet) still shows
								     something selectable instead of collapsing
								     to an empty box. Keyed on the resolved media
								     rather than the type, because the type is set
								     before the image is chosen. */ }
								{ label ||
									( ( mediaType === 'image' && mediaUrl ) ||
									( mediaType === 'icon' && iconName )
										? ''
										: `Tab ${ i + 1 }` ) }
							</Button>
						);
					} ) }
				</nav>
				<div { ...innerBlocksProps } />
			</div>
		</>
	);
}
